/**
 * Role-Based Access Control (RBAC) Configuration
 *
 * This file defines all roles and their permissions in the application.
 * To add a new role, simply add it to the ROLES object and define its permissions.
 */

// Define all available roles
const ROLES = {
  ADMIN: "admin",
  OPERATIONS: "operations",
  MANAGER: "manager", // Example for future role
  VIEWER: "viewer", // Example for future role
};

// Define all available resources/pages
export const RESOURCES = {
  DASHBOARD: "dashboard",
  FINANCE: "finance",
  SUPPLIER_PAYMENTS: "supplier_payments",
  EXPENSES: "expenses",
  PROFIT_REPORTS: "profit_reports",
  EMPLOYEES: "employees",
  ATTENDANCE: "attendance",
  LEAVE_REQUESTS: "leave_requests",
  USERS: "users",
  PRODUCTS: "products",
  PLATFORMS: "platforms",
  PRICING: "pricing",
  INVENTORY: "inventory",
  SALES: "sales",
  RETURNS: "returns",
  PURCHASES: "purchases",
  CHALLAN: "challan",
  MANUFACTURING: "manufacturing",
  ANALYTICS: "analytics",
};

// Define permissions for each role
const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: {
    // Admin has full access to everything
    canAccess: Object.values(RESOURCES),
    canCreate: Object.values(RESOURCES),
    canEdit: Object.values(RESOURCES),
    canDelete: Object.values(RESOURCES),
    canApprove: [
      RESOURCES.SALES,
      RESOURCES.RETURNS,
      RESOURCES.PURCHASES,
      RESOURCES.LEAVE_REQUESTS,
    ],
  },

  [ROLES.OPERATIONS]: {
    // Operations has limited access
    canAccess: [
      RESOURCES.PRODUCTS, // Read-only
      RESOURCES.PLATFORMS, // Read-only
      RESOURCES.PRICING, // Read-only
      RESOURCES.INVENTORY,
      RESOURCES.SALES,
      RESOURCES.RETURNS,
      RESOURCES.PURCHASES,
      RESOURCES.CHALLAN,
      RESOURCES.MANUFACTURING, // Read-only
      RESOURCES.EMPLOYEES, // Can view Employees section
      RESOURCES.LEAVE_REQUESTS, // Can view and apply leave requests
    ],
    canCreate: [
      RESOURCES.SALES,
      RESOURCES.RETURNS,
      RESOURCES.PURCHASES,
      RESOURCES.LEAVE_REQUESTS, // Can apply leave requests
      // RESOURCES.CHALLAN, // Removed - Operations can only view challans
      // RESOURCES.MANUFACTURING, // Removed - Operations can only view manufacturing
    ],
    canEdit: [RESOURCES.INVENTORY], // Removed MANUFACTURING - now read-only
    // Operations can only delete pending items
    canDelete: [
      RESOURCES.SALES, // Only pending
      RESOURCES.RETURNS, // Only pending
      RESOURCES.PURCHASES, // Only pending
      // LEAVE_REQUESTS not included - cannot delete applied leave requests
    ],
    canApprove: [], // Cannot approve anything
  },

  // Example: Future manager role
  [ROLES.MANAGER]: {
    canAccess: [
      RESOURCES.DASHBOARD,
      RESOURCES.EMPLOYEES,
      RESOURCES.ATTENDANCE,
      RESOURCES.LEAVE_REQUESTS,
      RESOURCES.INVENTORY,
      RESOURCES.SALES,
      RESOURCES.RETURNS,
      RESOURCES.PURCHASES,
      RESOURCES.ANALYTICS,
    ],
    canCreate: [RESOURCES.EMPLOYEES, RESOURCES.LEAVE_REQUESTS],
    canEdit: [RESOURCES.EMPLOYEES, RESOURCES.INVENTORY],
    canDelete: [],
    canApprove: [RESOURCES.LEAVE_REQUESTS],
  },

  // Example: Future viewer role (read-only)
  [ROLES.VIEWER]: {
    canAccess: [
      RESOURCES.DASHBOARD,
      RESOURCES.INVENTORY,
      RESOURCES.PRODUCTS,
      RESOURCES.MANUFACTURING,
      RESOURCES.ANALYTICS,
    ],
    canCreate: [],
    canEdit: [],
    canDelete: [],
    canApprove: [],
  },
};

/**
 * Check if a user role can access a resource
 * @param {string} userRole - The user's role
 * @param {string} resource - The resource to check
 * @returns {boolean}
 */
export const canAccess = (userRole, resource) => {
  if (!userRole || !ROLE_PERMISSIONS[userRole]) {
    return false;
  }
  return ROLE_PERMISSIONS[userRole].canAccess.includes(resource);
};

/**
 * Check if a user role can create in a resource
 * @param {string} userRole - The user's role
 * @param {string} resource - The resource to check
 * @returns {boolean}
 */
export const canCreate = (userRole, resource) => {
  if (!userRole || !ROLE_PERMISSIONS[userRole]) {
    return false;
  }
  return ROLE_PERMISSIONS[userRole].canCreate.includes(resource);
};

/**
 * Check if a user role can edit a resource
 * @param {string} userRole - The user's role
 * @param {string} resource - The resource to check
 * @returns {boolean}
 */
export const canEdit = (userRole, resource) => {
  if (!userRole || !ROLE_PERMISSIONS[userRole]) {
    return false;
  }
  return ROLE_PERMISSIONS[userRole].canEdit.includes(resource);
};

/**
 * Check if a user role can delete from a resource
 * @param {string} userRole - The user's role
 * @param {string} resource - The resource to check
 * @param {string} status - Optional: the status of the item (e.g., 'pending')
 * @returns {boolean}
 */
export const canDelete = (userRole, resource, status = null) => {
  if (!userRole || !ROLE_PERMISSIONS[userRole]) {
    return false;
  }

  const hasDeletePermission =
    ROLE_PERMISSIONS[userRole].canDelete.includes(resource);

  // For operations role, only allow deleting pending items
  if (userRole === ROLES.OPERATIONS && status) {
    return hasDeletePermission && status === "pending";
  }

  return hasDeletePermission;
};

/**
 * Check if a user role can approve items in a resource
 * @param {string} userRole - The user's role
 * @param {string} resource - The resource to check
 * @returns {boolean}
 */
export const canApprove = (userRole, resource) => {
  if (!userRole || !ROLE_PERMISSIONS[userRole]) {
    return false;
  }
  return ROLE_PERMISSIONS[userRole].canApprove.includes(resource);
};

/**
 * Get all permissions for a role
 * @param {string} userRole - The user's role
 * @returns {Object}
 */
export const getRolePermissions = (userRole) => {
  return (
    ROLE_PERMISSIONS[userRole] || {
      canAccess: [],
      canCreate: [],
      canEdit: [],
      canDelete: [],
      canApprove: [],
    }
  );
};

/**
 * Check if a role is admin
 * @param {string} userRole - The user's role
 * @returns {boolean}
 */
export const isAdmin = (userRole) => {
  return userRole === ROLES.ADMIN;
};

/**
 * Get list of resources a role can access
 * @param {string} userRole - The user's role
 * @returns {Array<string>}
 */
export const getAccessibleResources = (userRole) => {
  if (!userRole || !ROLE_PERMISSIONS[userRole]) {
    return [];
  }
  return ROLE_PERMISSIONS[userRole].canAccess;
};

/**
 * Check if any of the roles can access a resource
 * @param {Array<string>} roles - Array of roles to check
 * @param {string} resource - The resource to check
 * @returns {boolean}
 */
export const canAnyRoleAccess = (roles, resource) => {
  return roles.some((role) => canAccess(role, resource));
};

/**
 * Route definitions with their access requirements
 * Order matters - the first accessible route will be used as the default landing page
 */
export const ROUTE_MAP = [
  { path: "/dashboard", resource: RESOURCES.DASHBOARD },
  { path: "/products", resource: RESOURCES.PRODUCTS },
  { path: "/platforms", resource: RESOURCES.PLATFORMS },
  { path: "/pricing", resource: RESOURCES.PRICING },
  { path: "/inventory", resource: RESOURCES.INVENTORY },
  { path: "/sales", resource: RESOURCES.SALES },
  { path: "/returns", resource: RESOURCES.RETURNS },
  { path: "/purchases", resource: RESOURCES.PURCHASES },
  { path: "/manufacturing", resource: RESOURCES.MANUFACTURING },
  { path: "/challan", resource: RESOURCES.CHALLAN },
  { path: "/supplier-payments", resource: RESOURCES.SUPPLIER_PAYMENTS },
  { path: "/expenses", resource: RESOURCES.EXPENSES },
  { path: "/profit-reports", resource: RESOURCES.PROFIT_REPORTS },
  { path: "/employees", resource: RESOURCES.EMPLOYEES },
  { path: "/users", resource: RESOURCES.USERS },
];

/**
 * Get the first accessible route for a user role
 * @param {string} userRole - The user's role
 * @returns {string} - The first accessible route path
 */
export const getFirstAccessibleRoute = (userRole) => {
  if (!userRole) {
    return "/unauthorized";
  }

  for (const route of ROUTE_MAP) {
    if (canAccess(userRole, route.resource)) {
      return route.path;
    }
  }

  return "/unauthorized";
};

const rolePermissions = {
  ROLES,
  RESOURCES,
  canAccess,
  canCreate,
  canEdit,
  canDelete,
  canApprove,
  getRolePermissions,
  isAdmin,
  getAccessibleResources,
  canAnyRoleAccess,
  getFirstAccessibleRoute,
  ROUTE_MAP,
};

export default rolePermissions;
